import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  WalletProvider as WalletReactProvider,
  useBrowserWallet,
  walletI18nResources,
  type WalletTranslator,
} from "@nullaone/wallet-react";
import type { WalletCoreConfig } from "@nullaone/wallet-core";
import i18n from "i18next";
import { BrowserWalletModal } from "@/features/wallet/components/browser-wallet";
import { TransactionConfirmationDialog } from "@/features/wallet/components/TransactionConfirmationDialog";
import { useToast } from "../hooks/useToast";
import { solanaService } from "../services/solana";

/**
 * Resolve the WebAuthn rpId for this deployment.
 *
 * WebAuthn requires the rpId to equal the current document's hostname OR be
 * a registrable parent of it. Mirrors the resolution logic in
 * detrust-oracle/workers/frontend/src/providers/WalletProvider.tsx.
 *
 * Rules:
 *   - localhost / 127.0.0.1: rpId === hostname
 *   - *.betmebro.com / betmebro.com: rpId === "betmebro.com"
 *   - anything else: rpId === hostname (single-level)
 */
function resolveRpId(): string {
  if (typeof window === "undefined") return "betmebro.com";
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return hostname;
  }
  if (hostname === "betmebro.com" || hostname.endsWith(".betmebro.com")) {
    return "betmebro.com";
  }
  return hostname;
}

// Register the wallet-react i18n resources under the "wallet" namespace so
// the package's own keys (e.g. "connectWallet", "copyAddress") resolve.
// Idempotent across HMR thanks to addResourceBundle's overwrite semantics.
let walletNamespaceRegistered = false;
function ensureWalletNamespace(): void {
  if (walletNamespaceRegistered) return;
  walletNamespaceRegistered = true;
  for (const [lang, resources] of Object.entries(walletI18nResources)) {
    i18n.addResourceBundle(lang, "wallet", resources, true, true);
  }
}

/**
 * Wallet Error Toast Component
 *
 * Reads `error` off `useBrowserWallet` and surfaces it through the host's
 * branded toast. Stays inside the new `<WalletReactProvider>` so the hook
 * resolves.
 */
function WalletErrorToast() {
  const { error } = useBrowserWallet();
  const { error: showError } = useToast();

  useEffect(() => {
    if (error) {
      showError("Wallet Error", {
        description: error,
      });
    }
  }, [error, showError]);

  return null;
}

interface WalletProviderProps {
  children: React.ReactNode;
}

/**
 * Wallet Provider Component
 *
 * Wraps the app with `<WalletReactProvider>` from `@nullaone/wallet-react`,
 * supplying the host-owned RPC client, a toast adapter that fans out to the
 * branded toast, and the i18next translator scoped to the `wallet` namespace.
 *
 * Renders the betmebro-branded `<BrowserWalletModal />`,
 * `<TransactionConfirmationDialog />`, and `<WalletErrorToast />` underneath
 * so they continue to consume the wallet context.
 */
export function WalletProvider({ children }: WalletProviderProps) {
  ensureWalletNamespace();
  const { success, error } = useToast();
  const { t } = useTranslation("wallet");
  const rpc = solanaService.getRpc();

  // Override the wallet-core baseline rpId with one that matches this
  // frontend's origin. Stable across renders so WalletReactProvider doesn't
  // recreate the WalletCore on every paint.
  const walletConfig = useMemo<WalletCoreConfig>(
    () => ({
      webauthn: {
        rpId: resolveRpId(),
        rpName: "BetMeBro",
      },
    }),
    [],
  );

  // Widen the typed react-i18next `t` to the loose `(key, params?) => string`
  // shape wallet-react expects.
  const translator = useCallback<WalletTranslator>(
    (key, params) =>
      (t as unknown as (k: string, p?: Record<string, unknown>) => string)(
        key,
        params,
      ),
    [t],
  );

  // Stable adapter object — toast callbacks are stable, so memo keeps the
  // provider value referentially equal across renders.
  const toast = useMemo(
    () => ({
      success: (message: string) => success(message),
      error: (message: string) => error(message),
    }),
    [success, error],
  );

  return (
    <WalletReactProvider
      config={walletConfig}
      rpc={rpc}
      toast={toast}
      t={translator}
    >
      <WalletErrorToast />
      <BrowserWalletModal />
      <TransactionConfirmationDialog />
      {children}
    </WalletReactProvider>
  );
}

export default WalletProvider;
